import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings as SettingsIcon, DollarSign, Save, RotateCcw, Bell, Moon, Sun, Globe, Volume2, VolumeX, Shield, ChevronRight, Sparkles, User, LogOut, Trash2, MapPin, Users, GraduationCap, Building2, Clock, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useTheme, ThemeType } from '@/hooks/useTheme';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useNotificationPreferences } from '@/hooks/useNotificationPreferences';
import { EditProfileDialog } from '@/components/EditProfileDialog';
import { DeleteAccountDialog } from '@/components/DeleteAccountDialog';
import { Slider } from '@/components/ui/slider';

const DEFAULT_WARNING_THRESHOLD = 500000;
const DEFAULT_DANGER_THRESHOLD = 1000000;

export interface FeeThresholdSettings {
  warningThreshold: number;
  dangerThreshold: number;
}

export interface NotificationSettings {
  soundEnabled: boolean;
  matchReminders: boolean;
  bookingReminders: boolean;
  newFollowers: boolean;
}

export const getFeeThresholdSettings = (): FeeThresholdSettings => {
  try {
    const stored = localStorage.getItem('feeThresholdSettings');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to parse fee threshold settings:', e);
  }
  return {
    warningThreshold: DEFAULT_WARNING_THRESHOLD,
    dangerThreshold: DEFAULT_DANGER_THRESHOLD,
  };
};

export const getNotificationSettings = (): NotificationSettings => {
  try {
    const stored = localStorage.getItem('notificationSettings');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to parse notification settings:', e);
  }
  return {
    soundEnabled: true,
    matchReminders: true,
    bookingReminders: true,
    newFollowers: true,
  };
};

export const getLanguage = (): string => {
  return localStorage.getItem('language') || 'vi';
};

export default function Settings() {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { user, signOut } = useAuth();
  const { profile, updateProfile } = useProfile();
  const { preferences: smartPrefs, updatePreferences: updateSmartPrefs, isUpdating: isSmartUpdating } = useNotificationPreferences();
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  
  // Fee threshold settings
  const [warningThreshold, setWarningThreshold] = useState(DEFAULT_WARNING_THRESHOLD);
  const [dangerThreshold, setDangerThreshold] = useState(DEFAULT_DANGER_THRESHOLD);
  
  // Notification settings
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [matchReminders, setMatchReminders] = useState(true);
  const [bookingReminders, setBookingReminders] = useState(true);
  const [newFollowers, setNewFollowers] = useState(true);
  
  // Language setting
  const [language, setLanguage] = useState('vi');

  useEffect(() => {
    // Load fee threshold settings
    const feeSettings = getFeeThresholdSettings();
    setWarningThreshold(feeSettings.warningThreshold);
    setDangerThreshold(feeSettings.dangerThreshold);
    
    // Load notification settings
    const notifSettings = getNotificationSettings();
    setSoundEnabled(notifSettings.soundEnabled);
    setMatchReminders(notifSettings.matchReminders);
    setBookingReminders(notifSettings.bookingReminders);
    setNewFollowers(notifSettings.newFollowers);
    
    // Load language
    setLanguage(getLanguage());
  }, []);

  const handleSaveFeeSettings = () => {
    if (warningThreshold >= dangerThreshold) {
      toast.error('Ngưỡng cảnh báo phải nhỏ hơn ngưỡng nguy hiểm');
      return;
    }

    const settings: FeeThresholdSettings = {
      warningThreshold,
      dangerThreshold,
    };
    localStorage.setItem('feeThresholdSettings', JSON.stringify(settings));
    toast.success('Đã lưu cài đặt phí');
  };

  const handleResetFeeSettings = () => {
    setWarningThreshold(DEFAULT_WARNING_THRESHOLD);
    setDangerThreshold(DEFAULT_DANGER_THRESHOLD);
    localStorage.removeItem('feeThresholdSettings');
    toast.success('Đã khôi phục cài đặt phí mặc định');
  };
  
  const saveNotificationSettings = (updates: Partial<NotificationSettings>) => {
    const current = getNotificationSettings();
    const newSettings = { ...current, ...updates };
    localStorage.setItem('notificationSettings', JSON.stringify(newSettings));
  };
  
  const handleSoundToggle = (enabled: boolean) => {
    setSoundEnabled(enabled);
    saveNotificationSettings({ soundEnabled: enabled });
    toast.success(enabled ? 'Đã bật âm thanh thông báo' : 'Đã tắt âm thanh thông báo');
  };
  
  const handleMatchRemindersToggle = (enabled: boolean) => {
    setMatchReminders(enabled);
    saveNotificationSettings({ matchReminders: enabled });
  };
  
  const handleBookingRemindersToggle = (enabled: boolean) => {
    setBookingReminders(enabled);
    saveNotificationSettings({ bookingReminders: enabled });
  };
  
  const handleNewFollowersToggle = (enabled: boolean) => {
    setNewFollowers(enabled);
    saveNotificationSettings({ newFollowers: enabled });
  };
  
  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
    toast.success('Đã thay đổi ngôn ngữ');
  };
  
  const handleThemeChange = (newTheme: ThemeType) => {
    setTheme(newTheme);
    toast.success(
      newTheme === 'night' ? 'Đã chuyển sang chế độ ban đêm' : 
      newTheme === 'dark' ? 'Đã chuyển sang chế độ tối' : 
      'Đã chuyển sang chế độ sáng'
    );
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleAccountDeleted = async () => {
    await signOut();
    navigate('/');
    toast.success('Tài khoản đã được xóa');
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="flex items-center gap-3 px-4 py-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-primary" />
            <h1 className="text-lg font-bold">Cài đặt</h1>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Edit Profile */}
        {user && profile && (
          <Card 
            className="cursor-pointer hover:bg-accent/50 transition-colors"
            onClick={() => setEditProfileOpen(true)}
          >
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-sport-tennis/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-sport-tennis" />
                  </div>
                  <div>
                    <h3 className="font-medium text-sm">Chỉnh sửa hồ sơ</h3>
                    <p className="text-xs text-muted-foreground">
                      Cập nhật thông tin cá nhân, ảnh đại diện
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Privacy Settings Link */}
        <Card 
          className="cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => navigate('/settings/privacy')}
        >
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-sm">Quyền riêng tư</h3>
                  <p className="text-xs text-muted-foreground">
                    Kiểm soát ai có thể xem thông tin của bạn
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Moon className="w-5 h-5 text-primary" />
              <CardTitle className="text-base">Giao diện</CardTitle>
            </div>
            <CardDescription className="text-xs">
              Tùy chỉnh chế độ hiển thị của ứng dụng
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <Label className="text-sm">Chế độ hiển thị</Label>
              <p className="text-xs text-muted-foreground">
                Chọn giao diện phù hợp với sở thích của bạn
              </p>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant={theme === 'light' ? 'default' : 'outline'}
                  size="sm"
                  className={`h-auto py-3 flex-col gap-1.5 ${theme === 'light' ? 'ring-2 ring-primary' : ''}`}
                  onClick={() => handleThemeChange('light')}
                >
                  <Sun className="w-5 h-5" />
                  <span className="text-xs font-medium">Sáng</span>
                </Button>
                <Button
                  variant={theme === 'dark' ? 'default' : 'outline'}
                  size="sm"
                  className={`h-auto py-3 flex-col gap-1.5 ${theme === 'dark' ? 'ring-2 ring-primary' : ''}`}
                  onClick={() => handleThemeChange('dark')}
                >
                  <Moon className="w-5 h-5" />
                  <span className="text-xs font-medium">Tối</span>
                </Button>
                <Button
                  variant={theme === 'night' ? 'default' : 'outline'}
                  size="sm"
                  className={`h-auto py-3 flex-col gap-1.5 ${theme === 'night' ? 'ring-2 ring-primary' : ''}`}
                  onClick={() => handleThemeChange('night')}
                >
                  <Sparkles className="w-5 h-5" />
                  <span className="text-xs font-medium">Đêm</span>
                </Button>
              </div>
              <div className="mt-2 p-3 rounded-lg bg-muted/50 border border-border">
                <p className="text-xs text-muted-foreground">
                  {theme === 'light' && '☀️ Chế độ sáng - Phù hợp sử dụng ban ngày'}
                  {theme === 'dark' && '🌙 Chế độ tối - Dễ chịu cho mắt, tiết kiệm pin'}
                  {theme === 'night' && '✨ Chế độ đêm - Tối nhất, hiệu ứng neon nổi bật'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Language Settings */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-sport-badminton" />
              <CardTitle className="text-base">Ngôn ngữ</CardTitle>
            </div>
            <CardDescription className="text-xs">
              Chọn ngôn ngữ hiển thị của ứng dụng
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={language} onValueChange={handleLanguageChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Chọn ngôn ngữ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vi">
                  <div className="flex items-center gap-2">
                    <span>🇻🇳</span>
                    <span>Tiếng Việt</span>
                  </div>
                </SelectItem>
                <SelectItem value="en">
                  <div className="flex items-center gap-2">
                    <span>🇺🇸</span>
                    <span>English</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-2">
              Tính năng đa ngôn ngữ sẽ sớm được hỗ trợ đầy đủ
            </p>
          </CardContent>
        </Card>
        
        {/* Notification Settings */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-sport-volleyball" />
              <CardTitle className="text-base">Thông báo</CardTitle>
            </div>
            <CardDescription className="text-xs">
              Quản lý cài đặt thông báo của bạn
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {soundEnabled ? (
                  <Volume2 className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <VolumeX className="w-4 h-4 text-muted-foreground" />
                )}
                <div className="space-y-0.5">
                  <Label className="text-sm">Âm thanh thông báo</Label>
                  <p className="text-xs text-muted-foreground">
                    Phát âm thanh khi có thông báo mới
                  </p>
                </div>
              </div>
              <Switch
                checked={soundEnabled}
                onCheckedChange={handleSoundToggle}
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-sm">Nhắc nhở trận đấu</Label>
                <p className="text-xs text-muted-foreground">
                  Nhận thông báo trước khi trận đấu bắt đầu
                </p>
              </div>
              <Switch
                checked={matchReminders}
                onCheckedChange={handleMatchRemindersToggle}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-sm">Nhắc nhở đặt sân</Label>
                <p className="text-xs text-muted-foreground">
                  Nhận thông báo trước khi lịch đặt sân bắt đầu
                </p>
              </div>
              <Switch
                checked={bookingReminders}
                onCheckedChange={handleBookingRemindersToggle}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-sm">Người theo dõi mới</Label>
                <p className="text-xs text-muted-foreground">
                  Nhận thông báo khi có người theo dõi bạn
                </p>
              </div>
              <Switch
                checked={newFollowers}
                onCheckedChange={handleNewFollowersToggle}
              />
            </div>
          </CardContent>
        </Card>

        {/* Smart Notification Settings */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              <CardTitle className="text-base">Thông báo thông minh</CardTitle>
            </div>
            <CardDescription className="text-xs">
              Nhận gợi ý trận đấu, lớp học và sân phù hợp với bạn
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <div className="space-y-0.5">
                  <Label className="text-sm">Trận đấu gần bạn</Label>
                  <p className="text-xs text-muted-foreground">
                    Gợi ý trận đấu trong bán kính 5km
                  </p>
                </div>
              </div>
              <Switch
                checked={smartPrefs?.nearby_matches ?? true}
                onCheckedChange={(v) => updateSmartPrefs({ nearby_matches: v })}
                disabled={isSmartUpdating}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="w-4 h-4 text-muted-foreground" />
                <div className="space-y-0.5">
                  <Label className="text-sm">Trận đấu thiếu người</Label>
                  <p className="text-xs text-muted-foreground">
                    Trận sắp bắt đầu đang cần thêm người chơi
                  </p>
                </div>
              </div>
              <Switch
                checked={smartPrefs?.urgent_matches ?? true}
                onCheckedChange={(v) => updateSmartPrefs({ urgent_matches: v })}
                disabled={isSmartUpdating}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <GraduationCap className="w-4 h-4 text-muted-foreground" />
                <div className="space-y-0.5">
                  <Label className="text-sm">Lớp học mới</Label>
                  <p className="text-xs text-muted-foreground">
                    Thông báo khi có lớp học mới phù hợp
                  </p>
                </div>
              </div>
              <Switch
                checked={smartPrefs?.new_classes ?? true}
                onCheckedChange={(v) => updateSmartPrefs({ new_classes: v })}
                disabled={isSmartUpdating}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Building2 className="w-4 h-4 text-muted-foreground" />
                <div className="space-y-0.5">
                  <Label className="text-sm">Sân có lịch trống</Label>
                  <p className="text-xs text-muted-foreground">
                    Gợi ý sân gần bạn có lịch trống
                  </p>
                </div>
              </div>
              <Switch
                checked={smartPrefs?.court_promotions ?? true}
                onCheckedChange={(v) => updateSmartPrefs({ court_promotions: v })}
                disabled={isSmartUpdating}
              />
            </div>

            <Separator />

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <Label className="text-sm">Giờ yên tĩnh</Label>
              </div>
              <p className="text-xs text-muted-foreground">
                Không gửi thông báo thông minh trong khoảng thời gian này
              </p>
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  value={smartPrefs?.quiet_hours_start?.slice(0, 5) || '22:00'}
                  onChange={(e) => updateSmartPrefs({ quiet_hours_start: e.target.value })}
                  className="w-28"
                  disabled={isSmartUpdating}
                />
                <span className="text-sm text-muted-foreground">đến</span>
                <Input
                  type="time"
                  value={smartPrefs?.quiet_hours_end?.slice(0, 5) || '07:00'}
                  onChange={(e) => updateSmartPrefs({ quiet_hours_end: e.target.value })}
                  className="w-28"
                  disabled={isSmartUpdating}
                />
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <Label className="text-sm">Giới hạn thông báo thông minh/ngày</Label>
              <p className="text-xs text-muted-foreground">
                Tối đa {smartPrefs?.max_daily_smart_notifications ?? 5} thông báo gợi ý mỗi ngày
              </p>
              <Slider
                value={[smartPrefs?.max_daily_smart_notifications ?? 5]}
                onValueCommit={(v) => updateSmartPrefs({ max_daily_smart_notifications: v[0] })}
                min={1}
                max={10}
                step={1}
                disabled={isSmartUpdating}
              />
              <div className="flex justify-between text-[10px] text-muted-foreground">
                <span>1</span>
                <span>5</span>
                <span>10</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Fee Threshold Settings */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-sport-tennis" />
              <CardTitle className="text-base">Ngưỡng cảnh báo phí trận đấu</CardTitle>
            </div>
            <CardDescription className="text-xs">
              Tùy chỉnh ngưỡng hiển thị cảnh báo khi tổng phí dự kiến vượt quá mức này
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="warningThreshold" className="text-sm flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-rank-gold" />
                Ngưỡng cảnh báo (VND)
              </Label>
              <Input
                id="warningThreshold"
                type="number"
                min={0}
                step={50000}
                value={warningThreshold}
                onChange={(e) => setWarningThreshold(Number(e.target.value))}
                placeholder="500000"
              />
              <p className="text-[10px] text-muted-foreground">
                Hiển thị cảnh báo màu vàng khi tổng phí ≥ {new Intl.NumberFormat('vi-VN').format(warningThreshold)} VND
              </p>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="dangerThreshold" className="text-sm flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-destructive" />
                Ngưỡng nguy hiểm (VND)
              </Label>
              <Input
                id="dangerThreshold"
                type="number"
                min={0}
                step={100000}
                value={dangerThreshold}
                onChange={(e) => setDangerThreshold(Number(e.target.value))}
                placeholder="1000000"
              />
              <p className="text-[10px] text-muted-foreground">
                Hiển thị cảnh báo màu đỏ khi tổng phí ≥ {new Intl.NumberFormat('vi-VN').format(dangerThreshold)} VND
              </p>
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={handleSaveFeeSettings} className="flex-1 gap-2">
                <Save className="w-4 h-4" />
                Lưu cài đặt
              </Button>
              <Button variant="outline" onClick={handleResetFeeSettings} className="gap-2">
                <RotateCcw className="w-4 h-4" />
                Mặc định
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Sign Out */}
        {user && (
          <Card 
            className="cursor-pointer hover:bg-destructive/10 transition-colors border-destructive/20"
            onClick={handleSignOut}
          >
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                  <LogOut className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <h3 className="font-medium text-sm text-destructive">Đăng xuất</h3>
                  <p className="text-xs text-muted-foreground">
                    Thoát khỏi tài khoản của bạn
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delete Account */}
        {user && (
          <Card 
            className="cursor-pointer hover:bg-destructive/20 transition-colors border-destructive/30"
            onClick={() => setDeleteAccountOpen(true)}
          >
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-destructive/20 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <h3 className="font-medium text-sm text-destructive">Xóa tài khoản</h3>
                  <p className="text-xs text-muted-foreground">
                    Xóa vĩnh viễn tài khoản và tất cả dữ liệu
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Profile Dialog */}
      {profile && (
        <EditProfileDialog 
          open={editProfileOpen} 
          onOpenChange={setEditProfileOpen} 
          profile={profile} 
          onSave={updateProfile} 
        />
      )}

      {/* Delete Account Dialog */}
      {user && (
        <DeleteAccountDialog
          open={deleteAccountOpen}
          onOpenChange={setDeleteAccountOpen}
          userEmail={user.email || ''}
          onDeleted={handleAccountDeleted}
        />
      )}
    </div>
  );
}
